import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Activity as ActivityIcon,
  CalendarClock,
  ChevronDown,
  ImageDown,
  LayoutGrid,
  ListChecks,
  MountainSnow,
  Route,
  Sun,
  TrendingUp,
  Triangle,
  Trophy,
} from "lucide-react";
import { fetchProfileStats, fetchActivityHeatmap } from "../api/profile";
import { fetchActivities, type Activity } from "../api/activities";
import { PoweredByStrava, stravaActivityUrl } from "../components/StravaBrand";
import { ShareActivityDialog } from "../components/ShareActivityDialog";
import { EmptyState } from "../components/EmptyState";
import { fetchCollections } from "../api/collections";
import { companionNote, isCompanionCollection } from "@atlas/shared";
import { fetchEvents, type EventWithProgress } from "../api/events";
import { rarityColor, rarityLabel } from "../lib/rarity";
import { fetchOwnShowcase } from "../api/showcase";
import { AtlasMap } from "../components/map/AtlasMap";
import { computeFeatureCollectionBbox } from "../lib/geo";
import { LANDMARK_CATEGORIES } from "../lib/landmark-icons";
import { AtlasPanel } from "../components/AtlasPanel";
import { ScoreMeter } from "../components/ScoreMeter";
import {
  PeriodHeatGrid,
  HEAT_GRID_MODES,
  HEAT_GRID_WINDOW,
  type HeatGridMode,
} from "../components/HeatGrid";
import { StatBlock } from "../components/StatBlock";
import { ProgressBar } from "../components/ProgressBar";
import { CollectionCard, trailDistanceOf } from "../components/CollectionCard";
import { AchievementBadge } from "../components/AchievementBadge";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { Skel } from "../components/Skeleton";
import { UsernameClaimBanner } from "../components/UsernameClaimBanner";
import { OnboardingCard } from "../components/OnboardingCard";
import { EmailCaptureCard } from "../components/EmailCaptureCard";
import { HistoryGateCard } from "../components/HistoryGateNotice";

const noop = () => {};

const activityDateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
});

// One row of the dashboard's "Recent activities" panel: name + meta, then a
// Review link and the same gold Share (image render + signed link) as the
// review page header. Strava-sourced rows carry the Powered by Strava mark
// linking back to the source activity, as the brand guidelines require.
function RecentActivityRow({
  activity,
  onReview,
  onShare,
  className,
}: {
  activity: Activity;
  onReview: () => void;
  onShare: () => void;
  className?: string;
}) {
  const processing = activity.processedAt === null;
  return (
    <div
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "var(--radius-md)",
          background: "var(--surface-raised)",
          border: "1px solid var(--border-default)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-muted)",
        }}
      >
        <ActivityIcon size={15} />
      </span>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 14,
          rowGap: 4,
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {activity.name}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--text-muted)",
              marginTop: 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <span>
              {activityDateFmt.format(new Date(activity.startedAt))}
              {activity.distanceM != null && (
                <> · {(activity.distanceM / 1000).toFixed(1)} km</>
              )}
              {activity.elevationGainM != null &&
                activity.elevationGainM > 0 && (
                  <>
                    {" "}
                    ·{" "}
                    {Math.round(activity.elevationGainM).toLocaleString(
                      "en-GB",
                    )}{" "}
                    m
                  </>
                )}
              {processing && <> · Processing</>}
            </span>
          </div>
        </div>
        {activity.sourceType === "strava" && activity.sourceId && (
          <a
            href={stravaActivityUrl(activity.sourceId)}
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-flex", lineHeight: 0, flexShrink: 0 }}
            title="View on Strava"
          >
            <PoweredByStrava height={24} />
          </a>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ListChecks size={13} />}
          onClick={onReview}
        >
          Review
        </Button>
        <Button
          size="sm"
          leftIcon={<ImageDown size={13} />}
          onClick={onShare}
          disabled={processing}
        >
          Share
        </Button>
      </div>
    </div>
  );
}

// Sentence-case requirement line derived from the event's rule. Only activity
// inside the event window counts, so the window is stated alongside it.
function eventRequirement(event: EventWithProgress): string {
  const target = event.progress.target;
  switch (event.ruleType) {
    case "peak_count":
      return `Summit ${target} distinct peak${target === 1 ? "" : "s"}`;
    case "trail_section_count":
      return `Complete ${target} trail section${target === 1 ? "" : "s"}`;
    case "landmark_count":
      return `Visit ${target} landmark${target === 1 ? "" : "s"}`;
    case "total_distance":
      return `Cover ${(target / 1000).toLocaleString("en-GB", { maximumFractionDigits: 1 })} km of activity`;
    default:
      return `Reach ${target}`;
  }
}

const REWARD_KIND_LABEL: Partial<
  Record<EventWithProgress["rewardItems"][number]["kind"], string>
> = {
  badge: "Badge",
  banner: "Banner",
  title: "Title",
  map_theme: "Map theme",
  award: "Award",
  physical: "Merch",
  bundle: "Bundle",
  frame: "Avatar frame",
  trace_style: "Trace style",
  stamp: "Check-in stamp",
  pin: "Map pin",
};

const eventDateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

// Slim strip for an in-progress time-bound event (WS-11). Rendered above the
// rest of the dashboard, one per active event — stacked when there's more
// than one (rare: events rarely overlap). Clicking the strip expands it to
// show the requirement, the counting window, and the unlock rewards.
function EventBanner({ event }: { event: EventWithProgress }) {
  const [open, setOpen] = React.useState(false);
  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(event.endsAt).getTime() - Date.now()) / 86_400_000),
  );
  const isDistance = event.ruleType === "total_distance";
  const hasRewards = event.rewardWaypoints > 0 || event.rewardItems.length > 0;
  return (
    <div
      style={{
        borderRadius: "var(--radius-lg)",
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderLeft: "3px solid var(--accent)",
        boxShadow: "var(--ring-top)",
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 18,
          padding: "12px 18px",
          width: "100%",
          background: "none",
          border: "none",
          borderRadius: "var(--radius-lg)",
          cursor: "pointer",
          textAlign: "left",
          font: "inherit",
          color: "inherit",
        }}
      >
        <span
          style={{
            width: 36,
            height: 36,
            flex: "0 0 auto",
            borderRadius: "var(--radius-md)",
            display: "grid",
            placeItems: "center",
            background: "var(--accent-soft)",
            color: "var(--gold-400)",
            border: "1px solid var(--border-gold)",
          }}
        >
          <CalendarClock size={17} />
        </span>
        <div style={{ minWidth: 0, flex: "0 1 260px" }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "var(--text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.name}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--text-muted)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.description}
          </div>
        </div>
        <div style={{ flex: "1 1 160px", minWidth: 120, maxWidth: 360 }}>
          <ProgressBar
            value={event.progress.current}
            max={event.progress.target}
            size="sm"
            showValue
            valueFormat={
              isDistance
                ? (v, m) =>
                    `${(v / 1000).toFixed(1)} / ${(m / 1000).toFixed(1)} km`
                : undefined
            }
          />
        </div>
        <div
          style={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {event.completed ? (
            <Badge variant="gold" dot>
              Completed ✓
            </Badge>
          ) : (
            <span
              className="eyebrow"
              style={{ fontSize: 10, whiteSpace: "nowrap" }}
            >
              {daysLeft} day{daysLeft === 1 ? "" : "s"} left
            </span>
          )}
          <ChevronDown
            size={15}
            style={{
              color: "var(--text-muted)",
              flex: "0 0 auto",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          />
        </div>
      </button>
      {open && (
        <div
          className="r-wrap"
          style={{
            display: "flex",
            gap: 32,
            padding: "14px 18px 16px",
            borderTop: "1px solid var(--border-subtle)",
          }}
        >
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>
              Requirement
            </div>
            <div style={{ fontSize: 13, color: "var(--text-primary)" }}>
              {eventRequirement(event)}
            </div>
            <div
              style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}
            >
              Only activity between{" "}
              {eventDateFmt.format(new Date(event.startsAt))} and{" "}
              {eventDateFmt.format(new Date(event.endsAt))} counts.
            </div>
          </div>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>
              Unlock reward
            </div>
            {hasRewards ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {event.rewardWaypoints > 0 && (
                  <span
                    className="tnum"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--gold-400)",
                    }}
                  >
                    <Triangle
                      size={11}
                      fill="currentColor"
                      aria-hidden="true"
                    />
                    {event.rewardWaypoints.toLocaleString("en-GB")} Waypoints
                  </span>
                )}
                {event.rewardItems.map((item) => (
                  <div
                    key={item.id}
                    style={{ fontSize: 13, color: "var(--text-primary)" }}
                  >
                    {item.name}
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {" "}
                      · {REWARD_KIND_LABEL[item.kind] ?? item.kind}
                    </span>
                    {item.rarity !== "common" && (
                      <span
                        className="eyebrow"
                        style={{
                          fontSize: 9,
                          marginLeft: 8,
                          color: rarityColor(item.rarity),
                        }}
                      >
                        {rarityLabel(item.rarity)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                Bragging rights - this one is for the record only.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const HEAT_MODE_KEY = "atlas:heat-grid-mode";

/** Heat grid mode, default week, remembered per browser. */
function useHeatGridMode(): [HeatGridMode, (m: HeatGridMode) => void] {
  const [mode, setMode] = React.useState<HeatGridMode>(() => {
    try {
      const saved = window.localStorage.getItem(HEAT_MODE_KEY);
      if (saved === "day" || saved === "week" || saved === "month") return saved;
    } catch {
      // Storage blocked: fall back to the default.
    }
    return "week";
  });
  const select = React.useCallback((m: HeatGridMode) => {
    setMode(m);
    try {
      window.localStorage.setItem(HEAT_MODE_KEY, m);
    } catch {
      // Not remembered this time; the choice still applies.
    }
  }, []);
  return [mode, select];
}

function HeatModeControl({
  value,
  onChange,
}: {
  value: HeatGridMode;
  onChange: (m: HeatGridMode) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Group activity by"
      style={{
        display: "flex",
        gap: 2,
        padding: 2,
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 8,
      }}
    >
      {HEAT_GRID_MODES.map(({ mode, label }) => {
        const active = mode === value;
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(mode)}
            style={{
              height: 26,
              padding: "0 10px",
              border: 0,
              borderRadius: 6,
              background: active ? "var(--surface-hover)" : "transparent",
              color: active ? "var(--text-primary)" : "var(--text-secondary)",
              fontFamily: "var(--font-sans)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();

  const statsQuery = useQuery({
    queryKey: ["profile/stats"],
    queryFn: fetchProfileStats,
  });
  const heatmapQuery = useQuery({
    queryKey: ["profile/activity-heatmap"],
    // Wrapped so react-query's context object isn't passed as the year param.
    queryFn: () => fetchActivityHeatmap(),
  });
  const collectionsQuery = useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollections,
  });
  const eventsQuery = useQuery({
    queryKey: ["events"],
    queryFn: fetchEvents,
    staleTime: 60_000,
  });
  const showcaseQuery = useQuery({
    queryKey: ["dashboard/showcase"],
    queryFn: fetchOwnShowcase,
    staleTime: 60_000,
  });

  const activitiesQuery = useQuery({
    queryKey: ["activities", "dashboard-recent"],
    queryFn: () => fetchActivities(3, 0),
  });
  const [shareActivity, setShareActivity] = React.useState<Activity | null>(
    null,
  );

  const stats = statsQuery.data?.stats;
  const recentUnlocks = statsQuery.data?.recentUnlocks ?? [];
  const recentActivities = (activitiesQuery.data?.activities ?? []).slice(0, 3);
  const heatEntries = heatmapQuery.data?.data ?? null;
  const [heatMode, setHeatMode] = useHeatGridMode();
  // ATL-168: companion lists live inside their parent's page, not as tiles.
  const collections = (collectionsQuery.data?.collections ?? []).filter((c) => !isCompanionCollection(c));
  const activeEvents = (eventsQuery.data?.events ?? []).filter(
    (e) => e.status === "active",
  );

  const showcase = showcaseQuery.data?.data;
  // Frame the curated collected record only, like the full-screen page — the
  // heatmap's raw GPS traces can hold outlier coordinates that would stretch
  // the fit across the globe. Null with no record → UK construction bounds.
  const showcaseBounds = React.useMemo(
    () => (showcase ? computeFeatureCollectionBbox(showcase.features) : null),
    [showcase],
  );

  // Bottom row: the "Closest to completion" list stretches to match the
  // stacked right column. Three cards by default; a fourth is shown only if
  // the measured body height has room for it at the card's min-height.
  const closestCollections = [...collections]
    .filter((c) => c.completedCount < c.itemCount)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 4);
  const [closestBodyEl, setClosestBodyEl] =
    React.useState<HTMLDivElement | null>(null);
  const [showFourth, setShowFourth] = React.useState(false);
  React.useEffect(() => {
    if (!closestBodyEl || typeof ResizeObserver === "undefined") return;
    // 168px hero card min-height x4 + 3 gaps of 10px, plus a little slack so
    // the decision doesn't flap when the left column is the one setting the
    // row height (with four cards shown, the body is at least 702px).
    const NEEDED_FOR_FOUR = 4 * 168 + 3 * 10 + 8;
    const ro = new ResizeObserver(() => {
      setShowFourth(closestBodyEl.clientHeight >= NEEDED_FOR_FOUR);
    });
    ro.observe(closestBodyEl);
    return () => ro.disconnect();
  }, [closestBodyEl]);
  const visibleClosest = closestCollections.slice(0, showFourth ? 4 : 3);

  const statCards = [
    {
      label: "Summits",
      value: stats ? String(stats.totalSummits) : null,
      icon: <MountainSnow size={12} />,
    },
    {
      label: "Distance",
      value: stats
        ? `${(stats.totalDistanceM / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} km`
        : null,
      icon: <Route size={12} />,
    },
    {
      label: "Days out",
      value: stats ? String(stats.totalDaysOut) : null,
      icon: <Sun size={12} />,
    },
    {
      label: "Elevation gain",
      value: stats
        ? `${(stats.totalElevationGainM / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} km`
        : null,
      icon: <TrendingUp size={12} />,
    },
  ];

  return (
    <div
      className="r-page"
      style={{
        padding: 28,
        overflow: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {/* Free-plan history window; renders nothing on Pro or once dismissed. */}
      <HistoryGateCard hasActivities={recentActivities.length > 0} />
      <UsernameClaimBanner />

      {activeEvents.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {activeEvents.map((ev) => (
            <EventBanner key={ev.id} event={ev} />
          ))}
        </div>
      )}

      {/* hero row */}
      <div
        className="r-cols-1"
        style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 20 }}
      >
        {/* left column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {stats ? (
            <ScoreMeter
              score={stats.outdoorScore}
              level={stats.level}
              levelProgress={stats.levelProgress * 100}
              toNext={`${stats.toNextLevel.toLocaleString()} pts to next level`}
            />
          ) : (
            <Skel height="88px" />
          )}

          {/* Getting-started strip + email card sit under the hero (spec 29). */}
          <EmailCaptureCard deferToChecklist />
          <OnboardingCard />

          <div
            className="r-cols-2"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: 12,
            }}
          >
            {statCards.map(({ label, value, icon }) => (
              <div
                key={label}
                style={{
                  background: "var(--surface-card)",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: "var(--radius-lg)",
                  padding: 16,
                  boxShadow: "var(--ring-top)",
                }}
              >
                {value != null ? (
                  <StatBlock
                    size="sm"
                    label={label}
                    value={value}
                    icon={icon}
                  />
                ) : (
                  <Skel height="56px" />
                )}
              </div>
            ))}
          </div>

          <AtlasPanel
            title="Exploration activity"
            action={<HeatModeControl value={heatMode} onChange={setHeatMode} />}
          >
            <PeriodHeatGrid entries={heatEntries} mode={heatMode} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 14,
              }}
            >
              <span
                className="eyebrow"
                style={{ fontSize: 10, marginRight: "auto", whiteSpace: "nowrap" }}
              >
                {HEAT_GRID_WINDOW[heatMode]}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--text-muted)",
                }}
              >
                Less
              </span>
              {["--heat-0", "--heat-1", "--heat-2", "--heat-3", "--heat-4"].map(
                (h) => (
                  <span
                    key={h}
                    style={{
                      width: 11,
                      height: 11,
                      borderRadius: 3,
                      background: `var(${h})`,
                      display: "block",
                    }}
                  />
                ),
              )}
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--text-muted)",
                }}
              >
                More
              </span>
            </div>
          </AtlasPanel>
        </div>

        {/* Showcase — the same completed-only record the public page renders,
            embedded live (pan/zoom only; clicks are no-ops). */}
        <AtlasPanel
          title="Showcase"
          style={{ display: "flex", flexDirection: "column" }}
          bodyStyle={{
            padding: 0,
            flex: 1,
            minHeight: 320,
            position: "relative",
            overflow: "hidden",
            borderBottomLeftRadius: "var(--radius-lg)",
            borderBottomRightRadius: "var(--radius-lg)",
          }}
          action={
            <Button
              variant="ghost"
              size="sm"
              disabled={!showcaseQuery.data}
              onClick={() => {
                // Navigates to the real public URL (not an app route), same as
                // the map's showcase control.
                const url = showcaseQuery.data?.url;
                if (url) window.location.assign(url);
              }}
            >
              Full screen
            </Button>
          }
        >
          <AtlasMap
            features={showcase?.features ?? null}
            heatmap={showcase?.heatmap ?? null}
            selectedId={null}
            filter="completed"
            landmarkCategories={LANDMARK_CATEGORIES}
            onFeatureSelect={noop}
            onBoundsChange={noop}
            fitBounds={showcaseBounds}
            cluster={false}
            mapTheme={
              showcase?.equipped.mapTheme
                ? {
                    heatRamp: showcase.equipped.mapTheme.metadata.heatRamp,
                    traceColor: showcase.equipped.mapTheme.metadata.traceColor,
                  }
                : null
            }
            traceStyle={
              showcase?.equipped.traceStyle
                ? {
                    dashArray: showcase.equipped.traceStyle.metadata.dashArray,
                    glow: showcase.equipped.traceStyle.metadata.glow,
                  }
                : null
            }
            collectedPinColor={showcase?.equipped.pin?.metadata.color ?? null}
          />
        </AtlasPanel>
      </div>

      {/* bottom row */}
      <div
        className="r-cols-1"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
      >
        <AtlasPanel
          title="Closest to completion"
          style={{ display: "flex", flexDirection: "column" }}
          bodyStyle={{ flex: 1, display: "flex", flexDirection: "column" }}
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void navigate({ to: "/collections" })}
            >
              All collections
            </Button>
          }
        >
          <div
            ref={setClosestBodyEl}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {collectionsQuery.isLoading
              ? [0, 1, 2].map((i) => <Skel key={i} height="88px" />)
              : visibleClosest.map((c) => (
                  <CollectionCard
                    key={c.id}
                    className="fill-flex"
                    title={c.name}
                    type={c.type}
                    value={c.completedCount}
                    max={c.itemCount}
                    pct={c.pct}
                    distance={trailDistanceOf(c)}
                    note={companionNote(c.slug, collectionsQuery.data?.collections ?? [])}
                    image={c.coverImage}
                    icon={<LayoutGrid size={26} />}
                    onClick={() =>
                      void navigate(
                        // A one-trail collection is the trail page (ATL-132).
                        c.trailSlug
                          ? { to: "/trails/$id", params: { id: c.trailSlug } }
                          : { to: "/collections/$slug", params: { slug: c.slug } },
                      )
                    }
                  />
                ))}
          </div>
        </AtlasPanel>

        {/* Right column: recent activities stacked above recent unlocks; the
            r-cols-1 utility collapses the whole row on narrow screens. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            minWidth: 0,
          }}
        >
          <AtlasPanel
            title="Recent activities"
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
            bodyStyle={{ flex: 1, display: "flex", flexDirection: "column" }}
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void navigate({ to: "/activities", search: {} })}
              >
                All activities
              </Button>
            }
          >
            {activitiesQuery.isLoading ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[0, 1, 2].map((i) => (
                  <Skel key={i} height="52px" />
                ))}
              </div>
            ) : recentActivities.length === 0 ? (
              <EmptyState
                icon={<ActivityIcon size={18} />}
                title="No activities yet"
                hint="Import a GPX file or connect Strava on the Connected sources page."
              />
            ) : (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {recentActivities.map((a) => (
                  <RecentActivityRow
                    key={a.id}
                    className="fill-flex"
                    activity={a}
                    onReview={() =>
                      void navigate({
                        to: "/activities/$id/review",
                        params: { id: a.id },
                      })
                    }
                    onShare={() => setShareActivity(a)}
                  />
                ))}
              </div>
            )}
          </AtlasPanel>
          {shareActivity && (
            <ShareActivityDialog
              activityId={shareActivity.id}
              activityName={shareActivity.name}
              onClose={() => setShareActivity(null)}
            />
          )}

          <AtlasPanel
            title="Recent unlocks"
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
            bodyStyle={{ flex: 1, display: "flex", flexDirection: "column" }}
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void navigate({ to: "/achievements" })}
              >
                Trophy case
              </Button>
            }
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {statsQuery.isLoading
                ? [0, 1, 2].map((i) => <Skel key={i} height="84px" />)
                : recentUnlocks
                    .slice(0, 2)
                    .map((u) => (
                      <AchievementBadge
                        key={u.achievementId}
                        className="fill-flex"
                        title={u.name}
                        description={u.description}
                        tier={u.tier}
                        points={u.points}
                        unlocked
                        icon={<Trophy size={24} />}
                      />
                    ))}
            </div>
          </AtlasPanel>
        </div>
      </div>
    </div>
  );
}
